import { db } from '../firebase/config';
import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  query, 
  where, 
  getDocs, 
  Timestamp,
  serverTimestamp 
} from 'firebase/firestore';
import { auth } from '../firebase/config';

/**
 * VIP Level Configuration
 * Structure: VIP Level -> Sets × Tasks per Set = Total Tasks
 */
export const VIP_CONFIG = {
  'VIP1': { 
    optimizations: 3, 
    sets: 3, 
    tasks: 40, 
    tasksPerSet: 40,
    totalTasks: 120,
    commission: 0.4,
    color: '#3b82f6', // Blue
    badge: '🟦'
  },
  'VIP2': { 
    optimizations: 4, 
    sets: 4, 
    tasks: 45, 
    tasksPerSet: 45,
    totalTasks: 180,
    commission: 0.6,
    color: '#10b981', // Green
    badge: '🟩'
  },
  'VIP3': { 
    optimizations: 5, 
    sets: 5, 
    tasks: 50, 
    tasksPerSet: 50,
    totalTasks: 250,
    commission: 0.8,
    color: '#a855f7', // Purple
    badge: '🟪'
  },
  'VIP4': { 
    optimizations: 6, 
    sets: 6, 
    tasks: 55, 
    tasksPerSet: 55,
    totalTasks: 330,
    commission: 1.0,
    color: '#f59e0b', // Gold
    badge: '🟨'
  },
};

// Legacy support - keep for backward compatibility
export const getLegacyConfig = (vipLevel) => {
  const config = VIP_CONFIG[vipLevel] || VIP_CONFIG['VIP1'];
  return {
    tasksPerSet: config.tasks,
    setsPerDay: config.optimizations
  };
};

/**
 * Get today's date key (YYYY-MM-DD format)
 */
const getTodayKey = () => {
  const now = new Date();
  return now.toISOString().split('T')[0]; // Returns YYYY-MM-DD
};

/**
 * Get user's daily optimization status
 */
export const getUserOptimizationStatus = async (userId) => {
  try {
    const todayKey = getTodayKey();
    const statusRef = doc(db, 'optimizationStatus', userId);
    const statusDoc = await getDoc(statusRef);
    
    if (!statusDoc.exists()) {
      // Initialize new status
      return {
        isLocked: false,
        completedSets: 0,
        lastCompletedDate: null,
        pendingUnlock: false,
        setsPerDay: VIP_CONFIG['VIP1'].setsPerDay
      };
    }
    
    const data = statusDoc.data();
    const lastDate = data.lastCompletedDate?.toDate?.() || null;
    const lastDateKey = lastDate ? lastDate.toISOString().split('T')[0] : null;
    
    // Check if we need to reset (new day)
    if (lastDateKey !== todayKey) {
      // Reset for new day
      await updateDoc(statusRef, {
        completedSets: 0,
        isLocked: false,
        pendingUnlock: false,
        lastCompletedDate: null
      });
      
      return {
        isLocked: false,
        completedSets: 0,
        lastCompletedDate: null,
        pendingUnlock: false,
        setsPerDay: VIP_CONFIG[data.vipLevel || 'VIP1']?.setsPerDay || 3
      };
    }
    
    return {
      isLocked: data.isLocked || false,
      completedSets: data.completedSets || 0,
      lastCompletedDate: lastDate,
      pendingUnlock: data.pendingUnlock || false,
      setsPerDay: VIP_CONFIG[data.vipLevel || 'VIP1']?.setsPerDay || 3
    };
  } catch (error) {
    console.error('Error getting optimization status:', error);
    throw error;
  }
};

/**
 * Complete an optimization set
 */
export const completeOptimizationSet = async (userId, vipLevel) => {
  try {
    const todayKey = getTodayKey();
    const statusRef = doc(db, 'optimizationStatus', userId);
    const statusDoc = await getDoc(statusRef);
    
    const config = VIP_CONFIG[vipLevel] || VIP_CONFIG['VIP1'];
    const setsPerDay = config.setsPerDay;
    
    let currentCompletedSets = 0;
    let isLocked = false;
    
    if (statusDoc.exists()) {
      const data = statusDoc.data();
      const lastDate = data.lastCompletedDate?.toDate?.() || null;
      const lastDateKey = lastDate ? lastDate.toISOString().split('T')[0] : null;
      
      // Reset if new day
      if (lastDateKey !== todayKey) {
        currentCompletedSets = 1;
      } else {
        currentCompletedSets = (data.completedSets || 0) + 1;
      }
    } else {
      currentCompletedSets = 1;
    }
    
    // Check if reached daily limit
    const hasReachedLimit = currentCompletedSets >= setsPerDay;
    
    // Lock if not at final set (needs admin unlock) or if at limit (permanent lock)
    isLocked = true;
    
    // Create or update status
    const updateData = {
      completedSets: currentCompletedSets,
      isLocked: isLocked,
      lastCompletedDate: Timestamp.now(),
      vipLevel: vipLevel,
      updatedAt: serverTimestamp()
    };
    
    // If not at final set, create unlock request
    if (!hasReachedLimit) {
      updateData.pendingUnlock = true;
      // Create unlock request (legacy - using sets terminology)
      // Convert set number to optimization number for compatibility
      const completedOptimization = currentCompletedSets;
      const nextOptimization = currentCompletedSets + 1;
      await createUnlockRequest(userId, vipLevel, completedOptimization, nextOptimization);
    } else {
      updateData.pendingUnlock = false;
    }
    
    if (statusDoc.exists()) {
      await updateDoc(statusRef, updateData);
    } else {
      await setDoc(statusRef, {
        ...updateData,
        createdAt: serverTimestamp()
      });
    }
    
    return {
      completedSets: currentCompletedSets,
      setsPerDay: setsPerDay,
      isLocked: isLocked,
      hasReachedLimit: hasReachedLimit,
      needsUnlock: !hasReachedLimit
    };
  } catch (error) {
    console.error('Error completing optimization set:', error);
    throw error;
  }
};


/**
 * Get pending unlock requests (for admin)
 */
export const getPendingUnlockRequests = async () => {
  try {
    // Query only by status to avoid needing a composite index
    const q = query(
      collection(db, 'unlockRequests'),
      where('status', '==', 'pending')
    );
    
    const querySnapshot = await getDocs(q);
    const requests = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      // Filter out requests without requestedAt in memory
      if (data.requestedAt) {
        requests.push({
          id: doc.id,
          ...data
        });
      }
    });
    
    // Sort by requested time (newest first) in memory
    requests.sort((a, b) => {
      const timeA = a.requestedAt?.toDate?.() || new Date(0);
      const timeB = b.requestedAt?.toDate?.() || new Date(0);
      return timeB - timeA;
    });
    
    return requests;
  } catch (error) {
    console.error('Error getting unlock requests:', error);
    throw error;
  }
};

/**
 * Unlock optimization for user (admin action)
 */
export const unlockUserOptimization = async (requestId, userId, adminId, adminEmail) => {
  try {
    // Update unlock request
    const requestRef = doc(db, 'unlockRequests', requestId);
    await updateDoc(requestRef, {
      status: 'completed',
      unlockedAt: serverTimestamp(),
      unlockedBy: adminId,
      unlockedByEmail: adminEmail
    });
    
    // Update user's optimization status
    const statusRef = doc(db, 'optimizationStatus', userId);
    await updateDoc(statusRef, {
      isLocked: false,
      pendingUnlock: false,
      unlockedAt: serverTimestamp(),
      unlockedBy: adminId
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error unlocking user optimization:', error);
    throw error;
  }
};

/**
 * Save optimization progress (current task in current set)
 */
export const saveOptimizationProgress = async (userId, vipLevel, currentTask, currentSet) => {
  try {
    const todayKey = getTodayKey();
    const progressRef = doc(db, 'optimizationProgress', userId);
    
    await setDoc(progressRef, {
      vipLevel: vipLevel,
      currentTask: currentTask,
      currentSet: currentSet || 0,
      lastSavedDate: todayKey,
      lastSavedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }, { merge: true });
    
    return { success: true };
  } catch (error) {
    console.error('Error saving optimization progress:', error);
    throw error;
  }
};

/**
 * Get saved optimization progress
 */
export const getOptimizationProgress = async (userId) => {
  try {
    const todayKey = getTodayKey();
    const progressRef = doc(db, 'optimizationProgress', userId);
    const progressDoc = await getDoc(progressRef);
    
    if (!progressDoc.exists()) {
      return {
        currentTask: 0,
        currentSet: 0,
        hasProgress: false
      };
    }
    
    const data = progressDoc.data();
    const lastSavedDate = data.lastSavedDate || null;
    
    // Check if progress is from today
    if (lastSavedDate !== todayKey) {
      // Progress is from a different day, reset
      await updateDoc(progressRef, {
        currentTask: 0,
        currentSet: 0,
        lastSavedDate: todayKey,
        updatedAt: serverTimestamp()
      });
      
      return {
        currentTask: 0,
        currentSet: 0,
        hasProgress: false
      };
    }
    
    return {
      currentTask: data.currentTask || 0,
      currentSet: data.currentSet || 0,
      hasProgress: (data.currentTask || 0) > 0
    };
  } catch (error) {
    console.error('Error getting optimization progress:', error);
    throw error;
  }
};

/**
 * Clear optimization progress (when set is completed or reset)
 */
export const clearOptimizationProgress = async (userId) => {
  try {
    const progressRef = doc(db, 'optimizationProgress', userId);
    await updateDoc(progressRef, {
      currentTask: 0,
      currentSet: 0,
      updatedAt: serverTimestamp()
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error clearing optimization progress:', error);
    throw error;
  }
};

/**
 * Reset daily optimization (called at midnight)
 */
export const resetDailyOptimization = async (userId) => {
  try {
    const statusRef = doc(db, 'optimizationStatus', userId);
    const statusDoc = await getDoc(statusRef);
    
    if (statusDoc.exists()) {
      await updateDoc(statusRef, {
        completedSets: 0,
        isLocked: false,
        pendingUnlock: false,
        lastCompletedDate: null,
        updatedAt: serverTimestamp()
      });
    }
    
    // Also reset progress
    const progressRef = doc(db, 'optimizationProgress', userId);
    const progressDoc = await getDoc(progressRef);
    
    if (progressDoc.exists()) {
      await updateDoc(progressRef, {
        currentTask: 0,
        currentSet: 0,
        lastSavedDate: getTodayKey(),
        updatedAt: serverTimestamp()
      });
    }
  } catch (error) {
    console.error('Error resetting daily optimization:', error);
    throw error;
  }
};

/**
 * Get user's optimization data with individual optimization status
 */
export const getUserOptimizationData = async (userId, vipLevel) => {
  try {
    const todayKey = getTodayKey();
    const statusRef = doc(db, 'optimizationStatus', userId);
    const statusDoc = await getDoc(statusRef);
    
    const config = VIP_CONFIG[vipLevel] || VIP_CONFIG['VIP1'];
    const maxOptimizations = config.optimizations;
    
    // Initialize optimizations object
    const optimizations = {};
    for (let i = 1; i <= maxOptimizations; i++) {
      optimizations[i] = {
        status: 'locked',
        locked: true,
        progress: '0/0'
      };
    }
    
    if (!statusDoc.exists()) {
      // First optimization is auto-unlocked
      optimizations[1] = {
        status: 'unlocked',
        locked: false,
        progress: '0/' + config.tasks
      };
      
      return {
        userId,
        vipLevel,
        currentOptimization: 1,
        optimizations,
        currentTask: 0,
        tasksCompleted: 0,
        todaysEarnings: 0,
        today: todayKey
      };
    }
    
    const data = statusDoc.data();
    const savedToday = data.today || null;
    
    // Check if we need to reset (new day) - only reset if today field doesn't match today's date
    // This preserves progress during the same day
    if (savedToday && savedToday !== todayKey) {
      // Reset for new day - unlock all optimizations
      for (let i = 1; i <= maxOptimizations; i++) {
        optimizations[i] = {
          status: 'unlocked',
          locked: false,
          progress: '0/' + config.tasks
        };
      }
      
      await updateDoc(statusRef, {
        completedOptimizations: 0,
        currentOptimization: 1,
        optimizations: optimizations,
        currentTask: 0,
        tasksCompleted: 0,
        todaysEarnings: 0,
        isLocked: false,
        pendingUnlock: false,
        lastCompletedDate: null,
        today: todayKey,
        updatedAt: serverTimestamp()
      });
      
      return {
        userId,
        vipLevel,
        currentOptimization: 1,
        optimizations,
        currentTask: 0,
        tasksCompleted: 0,
        todaysEarnings: 0,
        today: todayKey
      };
    }
    
    // If today field doesn't exist, set it (first time or migration)
    if (!savedToday) {
      await updateDoc(statusRef, {
        today: todayKey,
        updatedAt: serverTimestamp()
      });
    }
    
    // Load existing data
    const existingOptimizations = data.optimizations || {};
    const currentOptimization = data.currentOptimization || 1;
    const currentTask = data.currentTask || 0;
    const tasksCompleted = data.tasksCompleted || 0;
    const todaysEarnings = data.todaysEarnings || 0;
    
    // Merge existing optimizations with defaults
    for (let i = 1; i <= maxOptimizations; i++) {
      if (existingOptimizations[i]) {
        optimizations[i] = existingOptimizations[i];
      } else if (i === 1) {
        // First optimization defaults to unlocked
        optimizations[i] = {
          status: 'unlocked',
          locked: false,
          progress: `${currentTask}/${config.tasks}`
        };
      }
    }
    
    return {
      userId,
      vipLevel,
      currentOptimization,
      optimizations,
      currentTask,
      tasksCompleted,
      todaysEarnings,
      today: data.today || todayKey
    };
  } catch (error) {
    console.error('Error getting user optimization data:', error);
    throw error;
  }
};

/**
 * Complete an optimization (after completing all tasks)
 */
export const completeOptimization = async (userId, optimizationNumber, vipLevel) => {
  try {
    const todayKey = getTodayKey();
    const statusRef = doc(db, 'optimizationStatus', userId);
    const statusDoc = await getDoc(statusRef);
    
    const config = VIP_CONFIG[vipLevel] || VIP_CONFIG['VIP1'];
    const maxOptimizations = config.optimizations;
    
    let data = {};
    if (statusDoc.exists()) {
      data = statusDoc.data();
    }
    
    const optimizations = data.optimizations || {};
    
    // Mark current optimization as completed
    optimizations[optimizationNumber] = {
      status: 'completed',
      locked: true,
      progress: `${config.tasks}/${config.tasks}`
    };
    
    // Check if there's a next optimization
    const nextOptimization = optimizationNumber + 1;
    const hasNextOptimization = nextOptimization <= maxOptimizations;
    
    if (hasNextOptimization) {
      // Lock next optimization (needs admin unlock)
      optimizations[nextOptimization] = {
        status: 'locked',
        locked: true,
        progress: '0/' + config.tasks
      };
      
      // Create unlock request
      await createUnlockRequest(userId, vipLevel, optimizationNumber, nextOptimization);
    }
    
    // Update status
    const updateData = {
      currentOptimization: hasNextOptimization ? nextOptimization : optimizationNumber,
      optimizations: optimizations,
      currentTask: 0,
      tasksCompleted: 0,
      isLocked: hasNextOptimization, // Lock if there's a next optimization
      pendingUnlock: hasNextOptimization,
      lastCompletedDate: Timestamp.now(),
      today: todayKey,
      updatedAt: serverTimestamp()
    };
    
    if (statusDoc.exists()) {
      await updateDoc(statusRef, updateData);
    } else {
      await setDoc(statusRef, {
        ...updateData,
        userId,
        vipLevel,
        createdAt: serverTimestamp()
      });
    }
    
    return {
      success: true,
      completedOptimization: optimizationNumber,
      nextOptimization: hasNextOptimization ? nextOptimization : null,
      needsUnlock: hasNextOptimization
    };
  } catch (error) {
    console.error('Error completing optimization:', error);
    throw error;
  }
};

/**
 * Unlock optimization for user (admin action)
 */
export const unlockOptimization = async (userId, optimizationNumber, adminId, adminEmail) => {
  try {
    const statusRef = doc(db, 'optimizationStatus', userId);
    const statusDoc = await getDoc(statusRef);
    
    if (!statusDoc.exists()) {
      throw new Error('User optimization status not found');
    }
    
    const data = statusDoc.data();
    const optimizations = data.optimizations || {};
    const config = VIP_CONFIG[data.vipLevel] || VIP_CONFIG['VIP1'];
    
    // Unlock the specified optimization
    optimizations[optimizationNumber] = {
      status: 'unlocked',
      locked: false,
      progress: '0/' + config.tasks
    };
    
    // Update unlock request if exists
    const requestsQuery = query(
      collection(db, 'unlockRequests'),
      where('userId', '==', userId),
      where('status', '==', 'pending')
    );
    const requestsSnapshot = await getDocs(requestsQuery);
    
    if (!requestsSnapshot.empty) {
      const requestDoc = requestsSnapshot.docs[0];
      await updateDoc(doc(db, 'unlockRequests', requestDoc.id), {
        status: 'completed',
        unlockedAt: serverTimestamp(),
        unlockedBy: adminId,
        unlockedByEmail: adminEmail,
        unlockedOptimization: optimizationNumber
      });
    }
    
    // Update user status
    await updateDoc(statusRef, {
      optimizations: optimizations,
      currentOptimization: optimizationNumber,
      isLocked: false,
      pendingUnlock: false,
      unlockedAt: serverTimestamp(),
      unlockedBy: adminId,
      updatedAt: serverTimestamp()
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error unlocking optimization:', error);
    throw error;
  }
};

/**
 * Update optimization progress (task completion)
 */
export const updateOptimizationProgress = async (userId, vipLevel, currentOptimization, currentTask, tasksCompleted) => {
  try {
    const todayKey = getTodayKey();
    const statusRef = doc(db, 'optimizationStatus', userId);
    const statusDoc = await getDoc(statusRef);
    
    const config = VIP_CONFIG[vipLevel] || VIP_CONFIG['VIP1'];
    
    let data = {};
    if (statusDoc.exists()) {
      data = statusDoc.data();
    }
    
    const optimizations = data.optimizations || {};
    
    // Update current optimization progress
    if (optimizations[currentOptimization]) {
      optimizations[currentOptimization].progress = `${currentTask}/${config.tasks}`;
    } else {
      optimizations[currentOptimization] = {
        status: 'unlocked',
        locked: false,
        progress: `${currentTask}/${config.tasks}`
      };
    }
    
    const updateData = {
      vipLevel,
      currentOptimization,
      optimizations,
      currentTask,
      tasksCompleted,
      today: todayKey,
      updatedAt: serverTimestamp()
    };
    
    if (statusDoc.exists()) {
      await updateDoc(statusRef, updateData);
    } else {
      await setDoc(statusRef, {
        ...updateData,
        userId,
        createdAt: serverTimestamp()
      });
    }
    
    return { success: true };
  } catch (error) {
    console.error('Error updating optimization progress:', error);
    throw error;
  }
};

/**
 * Create unlock request (updated to include optimization numbers)
 */
const createUnlockRequest = async (userId, vipLevel, completedOptimization, nextOptimization) => {
  try {
    const user = auth.currentUser;
    if (!user) return;
    
    const requestRef = doc(collection(db, 'unlockRequests'));
    await setDoc(requestRef, {
      userId: userId,
      userEmail: user.email || '',
      vipLevel: vipLevel,
      completedOptimization: completedOptimization,
      nextOptimization: nextOptimization,
      // Legacy field for backward compatibility
      completedSets: completedOptimization,
      status: 'pending',
      requestedAt: serverTimestamp(),
      unlockedAt: null,
      unlockedBy: null,
      unlockedByEmail: null
    });
  } catch (error) {
    console.error('Error creating unlock request:', error);
    // Don't throw - this is not critical
  }
};

